import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import { useClientProblemsRefresh } from "@/clients/hooks/useClientProblemsRefresh";
import { EncounterContext } from "@/contexts/encounter";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { EncounterProceduresScope } from "../constants/procedures";
import { ENCOUNTER_PROCEDURES_TAB_SECTION } from "../constants/unsaved-encounters";
import { useRegisterEncounterChildSave } from "../contexts/EncounterChildSaveContext";
import { saveEncounterProceduresAndConditions } from "../services/procedures";
import { StagedProceduresOperation } from "../types/procedures";
import { buildProceduresBundleContext, EncounterProceduresData } from "../utils/procedures";
import { applyStagedProceduresOperations } from "../utils/staged-procedures";

type UseProceduresStagingParams = {
  clientId: string | undefined;
  encounterId: string | undefined;
  /** The encounter as the last server read left it. The staged work is replayed on top of this. */
  serverData: EncounterProceduresData | undefined;
  /**
   * Re-reads the encounter. Called once the save has written, whether or not every operation landed,
   * and awaited, so it has to resolve on the read rather than only starting it.
   */
  reload: () => Promise<void>;
};

/**
 * The sections that stage here hold the encounter's clinical record, so they preview and write only
 * the resources that are not the biller section's — the same scope `EncounterProceduresProvider`
 * reads. The preview replays the write, and a preview built over a different set than the write
 * reconciles against would show the provider an encounter the save never produces.
 */
const PROCEDURES_STAGING_SCOPE = EncounterProceduresScope.Clinical;

type ProceduresStaging = {
  /** The encounter as the save is going to leave it: the given server read with the staged work replayed on top. */
  data: EncounterProceduresData | undefined;
  hasStagedChanges: boolean;
  stageOperation: (operation: StagedProceduresOperation) => void;
};

/**
 * Owns the encounter's staged procedure work: what the encounter holds, what the sections have
 * staged on top of it, and the write that lands when the encounter is saved.
 *
 * Nothing the provider does in the picker or in a removal reaches the server on its own. Sessions
 * are staged here and the encounter's Save or Sign & Lock — which arrives through the child-save
 * coordinator — is what writes them.
 *
 * Composed by `EncounterProceduresProvider` rather than called by a section: the read it stages on
 * top of, the child save it registers and the unsaved-changes check it keys are all one per
 * encounter, so a second caller would mean a second staged set that neither one previews.
 */
function useProceduresStaging({
  clientId,
  encounterId,
  serverData,
  reload,
}: UseProceduresStagingParams): ProceduresStaging {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { hasBillerRole } = usePractitionerPermissions();
  const { registerUnsavedCheck, unregisterUnsavedCheck } = useContext(EncounterContext);
  const refreshClientProblems = useClientProblemsRefresh(clientId);

  // The picker sessions and removals the sections have finished but the encounter has not saved yet.
  const [stagedOperations, setStagedOperations] = useState<StagedProceduresOperation[]>([]);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const landedOperationIdsRef = useRef(new Set<string>());

  // Staged work belongs to the encounter it was staged on. Moving straight from one encounter to the
  // next re-renders the provider with new ids instead of remounting it, so what the encounter being
  // left behind never saved is dropped here: kept, it would be previewed on top of the next
  // encounter's read and then written under that encounter's ids.
  const stagingKey = `${clientId}/${encounterId}`;
  const [stagedFor, setStagedFor] = useState(stagingKey);

  if (stagedFor !== stagingKey) {
    setStagedFor(stagingKey);
    setStagedOperations([]);
  }

  const hasStagedChanges = stagedOperations.length > 0;

  const bundleContext = useMemo(
    () =>
      clientId && encounterId
        ? buildProceduresBundleContext({
            profile,
            clientId,
            encounterId,
            isBiller: hasBillerRole,
            scope: PROCEDURES_STAGING_SCOPE,
          })
        : undefined,
    [clientId, encounterId, profile, hasBillerRole],
  );

  // Everything the encounter save is going to write, in the order it will write it. Built by running
  // the staged sessions through the same reconciliation the save uses, so the cards and the write
  // agree on what the encounter ends up holding.
  const data = useMemo(() => {
    if (!serverData || !bundleContext || !stagedOperations.length) return serverData;

    return applyStagedProceduresOperations({
      serverData,
      operations: stagedOperations,
      ...bundleContext,
    });
  }, [serverData, stagedOperations, bundleContext]);

  const stageOperation = useCallback((operation: StagedProceduresOperation) => {
    setStagedOperations((current) => [...current, operation]);
  }, []);

  // The encounter's Save and Sign & Lock both land here.
  //
  // Written one at a time, oldest first: each save re-reads the encounter and reconciles against it,
  // so an operation sees the resources the one before it wrote — the same order the sections
  // previewed them in. Running them together would have every operation reconcile against the
  // pre-save encounter and write over each other.
  //
  // Each one is dropped as it lands, so a retry after a failed write replays only what never reached
  // the server. Replaying one that did is not safe: a removal reconciles against a diagnosis the
  // first run retired, which the next read no longer returns, so a second run would recreate it.
  //
  // Saves never overlap. The encounter's auto-save and a Save click can each start one while the
  // other is still writing, and the second was handed a staged set from before the first dropped what
  // it wrote — so it waits its turn, then skips every operation that has already landed.
  //
  // Not a useCallback: useRegisterEncounterChildSave keeps the latest render's handler, so this
  // always reads the current staged set.
  const saveStagedOperations = () => {
    const operations = stagedOperations;
    const save = saveQueueRef.current.then(() => writeStagedOperations(operations));
    saveQueueRef.current = save.catch(() => undefined);
    return save;
  };

  const writeStagedOperations = async (operations: StagedProceduresOperation[]) => {
    const pendingOperations = operations.filter(({ id }) => !landedOperationIdsRef.current.has(id));
    if (!pendingOperations.length || !encounterId || !clientId) return;

    try {
      for (const operation of pendingOperations) {
        await saveEncounterProceduresAndConditions({
          medplum,
          clientId,
          encounterId,
          isBiller: hasBillerRole,
          scope: PROCEDURES_STAGING_SCOPE,
          formValues: operation.formValues,
          initialValues: operation.initialValues,
        });

        // Only this one is dropped, so sessions staged while the save was in flight survive it.
        landedOperationIdsRef.current.add(operation.id);
        setStagedOperations((current) => current.filter(({ id }) => id !== operation.id));
      }
    } catch (error) {
      // Swallowed rather than rethrown: the encounter's own save has already written by the time the
      // sections run, so failing the whole submit would not undo it, and on Sign & Lock it would
      // strand the provider on a locked encounter with no error. What did not land stays staged, so
      // the sections keep showing it as unsaved and the next save retries it.
      captureExceptionWithoutPhi("Failed to save the encounter's staged procedures", error, {
        encounterId,
      });
      toast({
        title: "Failed to save the procedures",
        message:
          "The rest of the encounter was saved. Your procedures are still here — try saving again, or contact support if the error persists.",
        type: "error",
        autoClose: 5000,
      });
    } finally {
      // Re-read either way, and wait for it. A save that failed partway still wrote the operations
      // that landed, and the ones left staged are previewed on top of this baseline. Awaited so the
      // encounter's save is not done until the cards are the encounter it just wrote.
      await reload();

      // A diagnosis this save retired is off the client's problem list, because the membership lives
      // on the diagnosis itself. The side panel's poll never drops a problem it stops seeing, so the
      // removal only reaches it if the list is re-read here.
      await refreshClientProblems();
    }
  };

  useRegisterEncounterChildSave(saveStagedOperations);

  // Joins the encounter's navigation blocker, so leaving with procedures the encounter has not saved
  // is warned about the same way an unsaved note is.
  useEffect(() => {
    registerUnsavedCheck?.(ENCOUNTER_PROCEDURES_TAB_SECTION, () => hasStagedChanges);

    return () => unregisterUnsavedCheck?.(ENCOUNTER_PROCEDURES_TAB_SECTION);
  }, [hasStagedChanges, registerUnsavedCheck, unregisterUnsavedCheck]);

  return { data, hasStagedChanges, stageOperation };
}

export default useProceduresStaging;
